import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Subscription } from 'rxjs';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import ErrorBoundary from 'view/error-boundary/error-boundary';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { MediaClientProvider } from '@atlaskit/media-client-react';
import { token } from '@atlaskit/tokens';
import { ConnectedKbSuggestedArticlesWrapper } from '@atlassian/help-center-common-component/fields/kb-suggestions-connected';
import type { RichTextAreaWrapperProps } from '@atlassian/help-center-common-component/rich-text-area-wrapper';
import { getRichTextAreaWrapperStyles } from '@atlassian/help-center-common-component/rich-text-area-wrapper';
import { useIsoMorphicLayoutEffect } from '@atlassian/help-center-common-util/hooks';
import { reportError } from '@atlassian/help-center-common-util/sentry';
import type { TemplateForm } from '@atlassian/proforma-common-core/form-system-models';
import type { BackendSettings } from '@atlassian/proforma-common-core/jira-common-models';
import type { PortalCreateSettings } from '@atlassian/proforma-jira-portal-create';
import { PortalCreateApp, PortalCreateLifecycleStage } from '@atlassian/proforma-jira-portal-create';
import { useOnFieldChange } from './services/use-on-field-change';
import type { OwnProps, StateProps, DispatchProps } from './types';

export type FormCreateAppContainerProps = OwnProps & StateProps & DispatchProps & WithAnalyticsEventsProps;

// A container to make sure FormCreateApp uses a serviceDeskId, projectId and proformaTemplateForm that are defined
export const FormCreateAppContainer = ({ ...props }: FormCreateAppContainerProps) => {
    const { projectId, serviceDeskId, proformaTemplateForm, renderFormFinished } = props;

    if (projectId === undefined || serviceDeskId === undefined) {
        return null;
    }

    if (!proformaTemplateForm) {
        reportError('The template form was undefined');
        return null;
    }

    return (
        <FormCreateApp
            {...{
                ...props,
                serviceDeskId,
                proformaTemplateForm,
                projectId,
            }}
            renderFormFinished={renderFormFinished}
        />
    );
};

// The same interface FormCreateAppContainerProps but with serviceDeskId, projectId and proformaTemplateForm defined
export type FormCreateAppOwnProps = FormCreateAppContainerProps & {
    serviceDeskId: number;
    projectId: number;
    proformaTemplateForm: TemplateForm;
};

export const FormCreateApp = ({
    locale,
    knowledgeBase,
    fetchKBSuggestionsSuccess,
    setProformaFormDirtyAction,
    portalId,
    createAnalyticsEvent,
    reporterAccountId,
    projectId,
    fieldErrors,
    backendSettingsWithoutContext,
    proformaTemplateForm,
    serviceDeskId,
    requestTypeId,
    mediaApiUploadToken,
    mediaApiError,
    blockFormSubmission,
    renderFormFinished,
    proformaFormIsDirty,
    fetchPortalUploadMediaToken,
    registerProformaForm: registerPortalFormCallback,
}: FormCreateAppOwnProps) => {
    const [subscription] = React.useState<Subscription>(new Subscription());
    const proformaFormIsDirtyRef = useRef<boolean>(proformaFormIsDirty);

    const onFieldChange = useOnFieldChange(
        createAnalyticsEvent,
        fetchKBSuggestionsSuccess,
        knowledgeBase,
        portalId,
        projectId,
        subscription
    );

    React.useEffect(() => {
        return () => {
            fetchKBSuggestionsSuccess({ portalId, kbArticleSuggestions: [] });
            subscription.unsubscribe();
            setProformaFormDirtyAction({ portalId, requestTypeId, isDirty: false });
        };
    }, [fetchKBSuggestionsSuccess, portalId, requestTypeId, setProformaFormDirtyAction, subscription]);

    React.useEffect(() => {
        // This triggers a call to start the media upload token refresh cycle if media upload token is undefined
        if (!mediaApiUploadToken) {
            fetchPortalUploadMediaToken(portalId);
        }
    }, [fetchPortalUploadMediaToken, mediaApiUploadToken, portalId]);

    useEffect(() => {
        proformaFormIsDirtyRef.current = proformaFormIsDirty;
    }, [proformaFormIsDirty]);

    const onFieldDirty = useCallback(() => {
        // REQIN-1695: We need to use a ref because PortalCreateApp will cache this function in the MobX store
        // so we can't update the callback without re-creating the store. We can remove this once MobX is removed
        if (!proformaFormIsDirtyRef.current) {
            setProformaFormDirtyAction({ portalId, requestTypeId, isDirty: true });
        }
    }, [portalId, proformaFormIsDirtyRef, requestTypeId, setProformaFormDirtyAction]);

    const [settings, setSettings] = React.useState<BackendSettings<PortalCreateSettings>>({
        ...backendSettingsWithoutContext,
        context: {
            onFieldChange,
            onFieldDirty,
            KbSuggestionField: ConnectedKbSuggestedArticlesWrapper,
            projectId: -1,
            portalId: -1,
            serviceDeskId: -1,
            requestTypeId: -1,
            intlLocale: locale,
            proformaTemplateForm: {} as TemplateForm,
            registerPortalFormCallback: () => undefined,
        },
    });

    useIsoMorphicLayoutEffect(() => {
        setSettings({
            ...backendSettingsWithoutContext,
            context: {
                projectId,
                portalId,
                serviceDeskId,
                requestTypeId,
                onFieldChange,
                onFieldDirty,
                proformaTemplateForm,
                registerPortalFormCallback,
                intlLocale: locale,
                KbSuggestionField: ConnectedKbSuggestedArticlesWrapper,
            },
        });
    }, [
        backendSettingsWithoutContext,
        locale,
        onFieldChange,
        onFieldDirty,
        portalId,
        proformaTemplateForm,
        projectId,
        registerPortalFormCallback,
        requestTypeId,
        serviceDeskId,
    ]);

    const proformaLifecycleHandlers = useMemo(() => {
        return {
            [PortalCreateLifecycleStage.FormRenderComplete]: () => {
                renderFormFinished();
            },
        };
        // We only want this to run once for now to prevent re-renders of the entire form. This is because
        // renderFormFinished is bound to the parent class with .bind(this) so it will always create a new function
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return settings.context.projectId > 0 &&
        settings.context.requestTypeId > 0 &&
        settings.context.serviceDeskId > 0 ? (
        <ErrorBoundary id="proforma-form-create-app" packageName="portal_fields">
            <Wrapper id="proforma-form-create-app">
                <MediaClientProvider
                    clientConfig={{
                        authProvider: () =>
                            Promise.resolve({
                                clientId: mediaApiUploadToken?.clientId || '',
                                token: mediaApiUploadToken?.token || '',
                                baseUrl: mediaApiUploadToken?.endpointUrl || '',
                            }),
                    }}
                >
                    <PortalCreateApp
                        settings={settings}
                        reporterAccountId={reporterAccountId}
                        jiraFieldErrors={fieldErrors}
                        mediaApiUploadToken={mediaApiUploadToken}
                        mediaApiError={mediaApiError}
                        blockFormSubmission={blockFormSubmission}
                        lifecycleHandlers={proformaLifecycleHandlers}
                    />
                </MediaClientProvider>
            </Wrapper>
        </ErrorBoundary>
    ) : null;
};

const wrapperMargin = `0 ${token('space.negative.100', '-8px')}`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Wrapper = styled.div<RichTextAreaWrapperProps>`
    margin: ${wrapperMargin};
    ${getRichTextAreaWrapperStyles('.ak-renderer-extension')}
`;
